import {
  dedupeVisibleEntryCards,
  extractEntryIDFromStarForm,
  initEntryReadObserver,
  isStarredOnlyView,
  isUnreadOnlyView,
  mergeAutoRefreshedUnreadEntries,
  parseDashboardURL,
  resetPendingUnreadRefreshQueue,
  syncDashboardLoadedEntryCount,
  syncDashboardReadModeControl
} from "./read_state.js";
import {
  shouldResetViewportForNavigation,
  syncNextInputForElement,
  syncNextInputForForm
} from "./app_events_navigation.js";
import {
  registerAccordionToggleHandler,
  registerDashboardInteractionHandlers
} from "./app_events_interactions.js";
import {
  registerDashboardBulkAppliedHandler,
  registerFeedCustomEventHandlers
} from "./app_events_feed_custom.js";
import {
  attachSidebarScrollPersistence,
  autoReloadEmptyFeedOnce,
  closeFeedMenuForElement,
  closeOverlayElement,
  focusActiveFeedInSidebar,
  highlightReaderContent,
  initDashboardSidebarToggle,
  isOverlayElement,
  restoreSidebarScrollPosition,
  saveSidebarScrollPosition,
  preserveSidebarScrollForNextNavigation,
  setOverlayOpenState,
  syncSidebarActiveState,
  syncEntriesAutoRefreshPath
} from "./sidebar_overlay.js";
import { initKeyboardShortcuts } from "./keyboard_shortcuts.js";
import { initDashboardViewControls } from "./dashboard_view.js";
import {
  initFeedAddSelectorShadowSync,
  initFeedAddSubmitProgress,
  initFeedPreviewTitleMirror,
  initSelectorPickerTargetFocusSync,
  initSelectorSnapshotPicker,
  setSelectorSnapshotLoadingMessage,
  syncSelectorSnapshotPickerTargetText
} from "./feed_add.js";

let shouldResetEntriesViewport = false;
let pendingAutoRefreshScrollY = null;
const pendingUnstarRemovalEntryIDs = new Set();
let eventWiringInitialized = false;

export function configureHtmxHistory() {
  if (!window.htmx) {
    return;
  }

  window.htmx.config.historyCacheSize = 0;

  try {
    Object.keys(window.localStorage).forEach(function (key) {
      if (key.indexOf("htmx-history-cache") === 0) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (_err) {
    // Ignore storage access failures.
  }
}

export function initAppEventWiring() {
  if (eventWiringInitialized) {
    return;
  }
  eventWiringInitialized = true;

  document.addEventListener("DOMContentLoaded", function () {
    resetPendingUnreadRefreshQueue();
    dedupeVisibleEntryCards();
    syncDashboardLoadedEntryCount();
    syncEntriesAutoRefreshPath();
    autoReloadEmptyFeedOnce();
    initFeedAddSubmitProgress();
    initFeedPreviewTitleMirror();
    initFeedAddSelectorShadowSync();
    initSelectorSnapshotPicker();
    initSelectorPickerTargetFocusSync();
    syncSelectorSnapshotPickerTargetText();
    syncSidebarActiveState();
    restoreSidebarScrollPosition();
    focusActiveFeedInSidebar();
    attachSidebarScrollPersistence();
    initDashboardSidebarToggle();
    initDashboardViewControls();
    syncDashboardReadModeControl();
    initEntryReadObserver();
    initKeyboardShortcuts();
    highlightReaderContent(document);
  });

  document.addEventListener("htmx:afterSwap", function (event) {
    const target = event.detail && event.detail.target ? event.detail.target : null;
    const htmlLength = target ? target.innerHTML.trim().length : 0;

    highlightReaderContent(target || document);

    if (target && isOverlayElement(target)) {
      setOverlayOpenState(target, htmlLength > 0);
    }

    if (target && target.id === "entries-content") {
      resetPendingUnreadRefreshQueue();
      dedupeVisibleEntryCards();
      if (pendingAutoRefreshScrollY !== null) {
        window.scrollTo({ top: pendingAutoRefreshScrollY, behavior: "auto" });
        pendingAutoRefreshScrollY = null;
      }

      if (shouldResetEntriesViewport) {
        shouldResetEntriesViewport = false;
        window.scrollTo({ top: 0, behavior: "auto" });
      }

      syncDashboardLoadedEntryCount();
      syncEntriesAutoRefreshPath();
      autoReloadEmptyFeedOnce();
      syncSidebarActiveState();
      restoreSidebarScrollPosition();
      focusActiveFeedInSidebar();
      attachSidebarScrollPersistence();
      syncDashboardReadModeControl();
      initEntryReadObserver();
      return;
    }

    if (target && target.classList && target.classList.contains("entries-load-more-sentinel")) {
      dedupeVisibleEntryCards();
      initEntryReadObserver();
    }

    if (target && target.classList && target.classList.contains("entries-sidebar")) {
      syncSidebarActiveState();
      restoreSidebarScrollPosition();
      focusActiveFeedInSidebar();
      attachSidebarScrollPersistence();
    }

    initSelectorPickerTargetFocusSync();
    syncSelectorSnapshotPickerTargetText();
    initFeedPreviewTitleMirror();
    initFeedAddSelectorShadowSync();
    initDashboardSidebarToggle();
    syncDashboardReadModeControl();
  });

  document.addEventListener("htmx:afterRequest", function (event) {
    if (!window.htmx) {
      return;
    }
    const detail = event.detail || {};
    const xhr = detail.xhr;
    if (!xhr || xhr.status < 200 || xhr.status >= 300) {
      return;
    }

    const pathInfo = detail.pathInfo || {};
    const requestPath = String(pathInfo.requestPath || "");
    if (!/^\/feeds\/\d+\/refresh$/.test(requestPath)) {
      return;
    }

    const entriesContent = document.getElementById("entries-content");
    if (!entriesContent) {
      return;
    }

    const current = window.location.pathname + window.location.search;
    window.htmx.ajax("GET", current, {
      target: "#entries-content",
      swap: "outerHTML",
      select: "#entries-content"
    });
  });

  document.addEventListener("htmx:beforeRequest", function (event) {
    saveSidebarScrollPosition();

    const detail = event.detail || {};
    const elt = detail.elt instanceof Element ? detail.elt : null;
    const target = detail.target instanceof Element ? detail.target : null;
    const pathInfo = detail.pathInfo || {};
    const requestPath = String(pathInfo.requestPath || "");

    syncNextInputForElement(elt);

    if (requestPath === "/feeds/add/selectors/snapshot") {
      setSelectorSnapshotLoadingMessage("Loading site snapshot...");
    }

    if (/^\/feeds\/\d+\/refresh$/.test(requestPath)) {
      closeFeedMenuForElement(elt);
    }

    if (/^\/feeds\/\d+\/remove$/.test(requestPath) && elt && elt.closest("form[data-preserve-sidebar-scroll='1']")) {
      preserveSidebarScrollForNextNavigation();
    }

    if (target && target.id === "entries-content") {
      const isAutoRefresh = elt && elt.id === "entries-auto-refresh";
      if (isAutoRefresh) {
        pendingAutoRefreshScrollY = window.scrollY;
      } else {
        pendingAutoRefreshScrollY = null;
        const currentURL = parseDashboardURL(window.location.pathname + window.location.search);
        const nextURL = parseDashboardURL(requestPath);
        if (shouldResetViewportForNavigation(currentURL, nextURL)) {
          shouldResetEntriesViewport = true;
        }
      }
    }

    if (elt instanceof Element) {
      const starForm = elt.closest("form[action^='/entries/'][action$='/star']");
      if (starForm && isStarredOnlyView()) {
        const starredInput = starForm.querySelector("input[name='starred']");
        const nextState = starredInput instanceof HTMLInputElement ? String(starredInput.value || "") : "";
        const entryID = extractEntryIDFromStarForm(starForm);
        if (nextState === "0") {
          if (entryID > 0) {
            pendingUnstarRemovalEntryIDs.add(entryID);
          }
        } else if (entryID > 0) {
          pendingUnstarRemovalEntryIDs.delete(entryID);
        }
      }
    }

    if (!(elt instanceof Element)) {
      return;
    }
    const form = elt.closest("form[data-feed-reload-status-id]");
    if (!form) {
      return;
    }
    const statusID = form.getAttribute("data-feed-reload-status-id");
    if (!statusID) {
      return;
    }
    const statusEl = document.getElementById(statusID);
    if (!statusEl) {
      return;
    }
    statusEl.textContent = "Reloading feed...";
    statusEl.classList.remove("text-red-700", "text-green-700");
    statusEl.classList.add("text-gray-600");
  });

  document.addEventListener("submit", function (event) {
    const form = event.target;
    syncNextInputForForm(form);
  }, true);

  document.addEventListener("htmx:beforeSwap", function (event) {
    const detail = event.detail || {};
    const target = detail.target instanceof Element ? detail.target : null;
    const elt = detail.elt instanceof Element ? detail.elt : null;
    const xhr = detail.xhr;
    if (!target || target.id !== "entries-content") {
      return;
    }
    if (!isUnreadOnlyView()) {
      return;
    }
    if (!xhr || typeof xhr.responseText !== "string") {
      return;
    }

    const isAutoRefresh = elt instanceof Element && elt.id === "entries-auto-refresh";
    const shouldPreserveVisibleCards = isAutoRefresh || !shouldResetEntriesViewport;
    if (!shouldPreserveVisibleCards) {
      return;
    }

    detail.shouldSwap = false;
    if (isAutoRefresh) {
      pendingAutoRefreshScrollY = null;
    }
    mergeAutoRefreshedUnreadEntries(xhr.responseText);
  });

  document.addEventListener("htmx:afterRequest", function (event) {
    const detail = event.detail || {};
    const elt = detail.elt instanceof Element ? detail.elt : null;
    if (!(elt instanceof Element)) {
      return;
    }

    const starForm = elt.closest("form[action^='/entries/'][action$='/star']");
    if (!starForm) {
      return;
    }

    const entryID = extractEntryIDFromStarForm(starForm);
    if (entryID <= 0) {
      return;
    }

    const xhr = detail.xhr;
    if (!xhr || xhr.status < 200 || xhr.status >= 300) {
      pendingUnstarRemovalEntryIDs.delete(entryID);
      return;
    }

    if (!pendingUnstarRemovalEntryIDs.has(entryID)) {
      return;
    }

    pendingUnstarRemovalEntryIDs.delete(entryID);
    const card = document.querySelector(".entry-grid-card[data-entry-id='" + String(entryID) + "']");
    if (card && card.parentElement) {
      card.parentElement.removeChild(card);
      syncDashboardLoadedEntryCount();
    }
  });

  document.addEventListener("htmx:afterRequest", function (event) {
    const elt = event.detail && event.detail.elt ? event.detail.elt : null;
    if (!(elt instanceof Element)) {
      return;
    }
    const form = elt.closest("form[data-feed-reload-status-id]");
    if (!form) {
      return;
    }

    const statusID = form.getAttribute("data-feed-reload-status-id");
    if (!statusID) {
      return;
    }
    const statusEl = document.getElementById(statusID);
    if (!statusEl) {
      return;
    }

    const xhr = event.detail && event.detail.xhr ? event.detail.xhr : null;
    if (xhr && xhr.status >= 200 && xhr.status < 300) {
      statusEl.textContent = "Feed reload finished.";
      statusEl.classList.remove("text-red-700", "text-gray-600");
      statusEl.classList.add("text-green-700");
      return;
    }

    statusEl.textContent = "Feed reload failed. Try again.";
    statusEl.classList.remove("text-green-700", "text-gray-600");
    statusEl.classList.add("text-red-700");
  });

  registerAccordionToggleHandler();
  registerFeedCustomEventHandlers(saveSidebarScrollPosition);
  registerDashboardInteractionHandlers(function () {
    shouldResetEntriesViewport = true;
  });
  registerDashboardBulkAppliedHandler();
}
